import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Cosmetic } from "../models/Cosmetic";
import { types } from "../enums/TypesEnum";
import { Button, Row, Col, Input, ButtonGroup } from "reactstrap";
import Image from "./Image";
import CreateModal from "./modals/CreateModal";
import { accountService } from "../services/accountService";
import { cosmeticService } from "../services/cosmeticService";
import { SortBy } from "../enums/SortTypeEnum";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faStar } from "@fortawesome/free-solid-svg-icons";
import CosmeticFilters from "./CosmeticFilters";

const Home = () => {
  const navigate = useNavigate();
  const [cosmetics, setCosmetics] = useState<Cosmetic[]>([]);
  const [orderType, setOrderType] = useState<SortBy>("UpdatedAt");
  const [pickedCategory, setPickedCategory] = useState<string>();
  const [pickedType, setPickedType] = useState<string>();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [createModal, setCreateModal] = useState<boolean>(false);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const user = accountService.userValue;

  const cosmeticClickHandler = (name: string) => navigate(`/cosmetic/${name}`);

  const populateData = async (reset = false, search?: string) => {
    setLoading(true);
    const data = await cosmeticService.getFilteredCosmetics(
      orderType,
      reset ? 1 : page,
      pickedCategory,
      pickedType,
      search
    );

    if (reset) {
      setCosmetics(data);
      setPage(1);
    } else {
      setCosmetics((prev) => [...prev, ...data]);
    }

    setHasMore(data.length === 15);
    setLoading(false);
  };

  // Reset danych przy zmianie filtrów/sortowania
  useEffect(() => {
    populateData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, pickedCategory, pickedType]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: "200px", // łap zanim dojedziesz do końca
        threshold: 0.1,
      }
    );

    const current = observerRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasMore, loading]);

  // Fetch kolejnych stron
  useEffect(() => {
    if (page > 1) {
      populateData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Debounce dla wyszukiwarki
  const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filter = e.target.value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      if (filter.length > 3) {
        populateData(true, filter);
      } else if (filter.length === 0) {
        populateData(true);
      }
    }, 500);
  };

  return (
    <div style={{ position: "relative" }}>
      <div>
        <div
          className="text-dark shadow-box"
          style={{
            background: "#fae4e7",
            color: "black",
            width: "100%",
            display: "inline-block",
            fontSize: 20,
            padding: 10,
          }}
        >
          Recenzje kosmetyków
        </div>
        <Row>
          <Col sm={{ offset: 3, size: 6 }} xs={12}>
            <Input
              className="search"
              onChange={searchHandler}
              type="text"
              placeholder="Wyszukaj"
            />
          </Col>
          <Col sm={{ offset: 1, size: 2 }} xs={12}>
            {user && (
              <Button
                style={{ padding: 10, width: "100%" }}
                onClick={() => setCreateModal(true)}
              >
                <FontAwesomeIcon
                  color="lightgrey"
                  style={{ marginRight: 5 }}
                  icon={faPlus}
                />
                Dodaj
              </Button>
            )}
          </Col>
        </Row>
        <CosmeticFilters
          pickedCategory={pickedCategory}
          pickedType={pickedType}
          setPickedCategory={setPickedCategory}
          setPickedType={setPickedType}
        />
        <Row style={{ marginTop: 15 }}>
          <Col sm={{ offset: 7, size: 5 }} xs={12}>
            <ButtonGroup>
              <Button
                style={{
                  backgroundColor:
                    orderType === "UpdatedAt" ? "#ad9faf" : "transparent",
                }}
                onClick={() => setOrderType("UpdatedAt")}
                active={orderType === "UpdatedAt"}
              >
                Najnowsze
              </Button>
              <Button
                style={{
                  backgroundColor:
                    orderType === "AverageGrade" ? "#ad9faf" : "transparent",
                }}
                onClick={() => setOrderType("AverageGrade")}
                active={orderType === "AverageGrade"}
              >
                Najwyższa ocena
              </Button>
              <Button
                style={{
                  backgroundColor:
                    orderType === "NoOfReviews" ? "#ad9faf" : "transparent",
                }}
                onClick={() => setOrderType("NoOfReviews")}
                active={orderType === "NoOfReviews"}
              >
                Popularne
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      <CreateModal
        toggle={createModal}
        setToggle={setCreateModal}
        setCosmetics={setCosmetics}
        cosmetics={cosmetics}
      />

      {loading && page === 1 ? (
        <div className="spinner" />
      ) : (
        <>
          <div className="wrap">
            {cosmetics.map((cosmetic: Cosmetic, index) => (
              <div
                className="shadow-box home-tile"
                key={index}
                onClick={() => cosmeticClickHandler(cosmetic.guid)}
              >
                <Row style={{ height: 20 }}>
                  <h6>
                    {cosmetic?.brand} -{" "}
                    {
                      types
                        .find((x) => x.category === cosmetic?.category)
                        ?.values.find((xx) => xx.value === cosmetic?.type)
                        ?.translations.find((xxx) => xxx.language === "pl")
                        ?.name
                    }
                  </h6>
                </Row>
                <Row style={{ height: 60 }}>
                  <h6 style={{ fontWeight: "bold", padding: "10" }}>
                    {cosmetic?.name}
                  </h6>
                </Row>
                <Row>
                  <Image guid={cosmetic?.guid} category={cosmetic?.category} />
                </Row>
                <Row style={{ marginTop: "5%" }}>
                  <p style={{ width: "100%", textAlign: "center" }}>
                    Ocena: <b>{cosmetic.averageGrade}</b>{" "}
                    <FontAwesomeIcon color="lightgrey" icon={faStar} />
                  </p>
                </Row>
              </div>
            ))}
          </div>

          {/* Strażnik do infinite scroll */}
          <div ref={observerRef} style={{ height: "1px" }} />

          {/* Spinner przy doładowywaniu */}
          {loading && page > 1 && <div className="spinner" />}
        </>
      )}
    </div>
  );
};

export default Home;




//const getLatestComment = (cosmetic: Cosmetic) => cosmetic.colors.map(x => x.grades.sort((a, b) => a.createDate.getTime() - b.createDate.getTime())[x.grades.length - 1])
//    .sort((a, b) => a.createDate.getTime() - b.createDate.getTime()).slice(-1)[0];

//{
//    cosmetics.map((cosmetic: Cosmetic, index) =>
//        <div key={index} onClick={() => cosmeticClickHandler(cosmetic.name)} className="tile" style={{ width: '100' }}>
//            <img style={{ width: '100%', objectFit: 'cover' }} src={cosmetic.image ? cosmetic?.image
//                : (categories.find(x => x.value === cosmetic.category)?.image ?? image)} alt="" />
//            <div className="text">
//                <h1>{cosmetic.brand} {cosmetic.name}</h1>
//                <h2 className="animate-text">{categories.find(x => x.value === cosmetic.category)?.translations.find(x => x.language === language)?.name ?? cosmetic.category} - {cosmetic.type}</h2>
//                <p className="animate-text">{`${getLatestComment(cosmetic).comment.substring(0, 80)}${getLatestComment(cosmetic).comment.length >= 80 ? '...' : ''} `}</p>
//            </div>
//        </div>
//    )
//}
